import { Link } from 'react-router-dom';
import BrandMark from '../components/BrandMark';
import Icon from '../components/Icon';
import { useAuth } from '../context/AuthContext';

// The marketing landing page that lives at https://outglow.live/.
// Dark-locked, brand-aligned, dense enough that it can stand on its
// own without screenshots from the app. Mirrors the mobile theme's
// pink → orange → amber glow gradient as the visual anchor.
export default function LandingPage() {
  const { user } = useAuth();

  return (
    <div className="landing">
      <nav className="land-nav">
        <div className="container row" style={{ justifyContent: 'space-between' }}>
          <BrandMark size={32} />
          <div className="row" style={{ gap: 6 }}>
            <a href="#how" className="land-nav-link">How it works</a>
            <a href="#features" className="land-nav-link">Features</a>
            <a href="#reputation" className="land-nav-link">Reputation</a>
            <Link to={user ? '/app' : '/login'} className="btn btn-ghost btn-sm">{user ? 'Open app' : 'Sign in'}</Link>
            <Link to={user ? '/app' : '/register'} className="btn btn-primary btn-sm">{user ? 'Open app' : 'Join free'}</Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ──────────────────────────────────────────── */}
      <section className="hero">
        <div className="container hero-inner">
          <div className="hero-copy">
            <span className="pill pill-accent">Public beta · 2026</span>
            <h1 className="h1">
              Post. <span className="grad-text">Challenge.</span> Vote.
            </h1>
            <p className="hero-lede">
              Outglow is a creator network where photos and videos go head-to-head and
              the crowd picks the winner. Climb tiers by winning battles. Lose them and
              you still take home reputation for showing up.
            </p>
            <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
              <Link to={user ? '/app' : '/register'} className="btn btn-primary btn-lg">
                {user ? 'Open the app →' : 'Create your account →'}
              </Link>
              <Link to={user ? '/app/battles' : '/login'} className="btn btn-ghost btn-lg">
                {user ? 'See live battles' : 'Sign in'}
              </Link>
            </div>
            <div className="row" style={{ gap: 24, marginTop: 24, flexWrap: 'wrap' }}>
              <Stat label="Active battles" value="Live" tone="primary" />
              <Stat label="Tiers" value="6" />
              <Stat label="Vote weighting" value="Rep-aware" />
              <Stat label="Resolution" value="24h auto" />
            </div>
          </div>
          <div className="hero-art">
            <HeroVisual />
          </div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────── */}
      <section className="section" id="how">
        <div className="container">
          <p className="eyebrow">How it works</p>
          <h2 className="h2">Three steps. Real stakes. No noise.</h2>
          <div className="how-grid">
            <Step n={1} title="Post your shot" body="Drop a photo or short video. Use it as a normal post or stage it for a battle." icon="image" mockup={<MockForge />} />
            <Step n={2} title="Pick a target" body="Tap Challenge on someone else's post. The battle goes live the instant you launch it — no accept step, no waiting." icon="flash" mockup={<MockChallenge />} />
            <Step n={3} title="The crowd decides" body="Anyone except the two creators can vote. Higher-reputation voters carry more weight. After 24h, results lock and rep is paid out." icon="trophy" mockup={<MockVote />} />
          </div>
        </div>
      </section>

      {/* ── Features grid ─────────────────────────────────── */}
      <section className="section" id="features">
        <div className="container">
          <p className="eyebrow">Built for creators</p>
          <h2 className="h2">A real product. Not a feed of likes.</h2>
          <div className="feature-grid">
            <Feature icon="battles" title="Targeted, instant battles" body="Every challenge picks a specific post and goes live on the spot. One challenge per target — no spam." />
            <Feature icon="trophy" title="Weighted voting" body="Rep tiers (Spark → Beacon → Pulse → Aurora → Halo → Ascendant) modulate vote weight. Trolls and brand-new accounts can't tip a result." />
            <Feature icon="flash" title="Reputation that compounds" body="Win to climb, lose to participate. Reach milestone tiers and unlock bonus rewards baked into the resolver." />
            <Feature icon="history" title="Live leaderboards" body="Reputation, wins, and trending creators — all refreshed continuously from the same engine that runs the app." />
            <Feature icon="shield" title="Safety by default" body="Content scanning, report queues, block lists, suspended-account hiding, idempotent voting and DM moderation tooling baked in." />
            <Feature icon="chat" title="Native mobile + web" body="iOS / Android via Expo, web at outglow.live/app. Same accounts, same battles, in real time across all of them." />
          </div>
        </div>
      </section>

      {/* ── Reputation system ─────────────────────────────── */}
      <section className="section section-alt" id="reputation">
        <div className="container">
          <p className="eyebrow">Reputation engine</p>
          <h2 className="h2">Earn it. Spend it. Wear it.</h2>
          <p className="muted" style={{ maxWidth: 640, marginTop: 8 }}>
            Reputation is the spine of the product. Every battle outcome — win, loss, tie — pays out
            a measured amount of rep. Voting carries weight based on the voter's tier. Climb the
            ladder, become a king-maker.
          </p>
          <div className="tier-row">
            <Tier name="Spark"      rep="0+"       color="#5C546C" />
            <Tier name="Beacon"     rep="100+"     color="#A89FB8" />
            <Tier name="Pulse"      rep="500+"     color="#5BE9FF" />
            <Tier name="Aurora"     rep="2,000+"   color="#FFB347" />
            <Tier name="Halo"       rep="6,000+"   color="#FF6B4A" />
            <Tier name="Ascendant"  rep="15,000+"  color="#FF3E7F" />
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────── */}
      <section className="section">
        <div className="container">
          <div className="cta-card">
            <div>
              <p className="eyebrow">Ready when you are</p>
              <h2 className="h2">Get on the board.</h2>
              <p className="muted" style={{ marginTop: 8 }}>Free account. Two clicks to your first post. About sixty seconds to your first challenge.</p>
            </div>
            <div className="row" style={{ gap: 10 }}>
              <Link to={user ? '/app' : '/register'} className="btn btn-primary btn-lg">
                {user ? 'Open the app' : 'Create account'}
              </Link>
              <Link to={user ? '/app/battles' : '/login'} className="btn btn-ghost btn-lg">
                {user ? 'Live battles' : 'Sign in'}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <BrandMark size={24} />
          <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
            <Link to="/explore">Explore</Link>
            <Link to="/leaderboard">Leaderboard</Link>
            <Link to="/how-it-works">How it works</Link>
            <Link to="/about">About</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/guidelines">Guidelines</Link>
            <a href="/legal/terms">Terms</a>
            <a href="/legal/privacy">Privacy</a>
            <a href="/legal/csae">Child Safety</a>
            <a href="/legal/dmca">DMCA</a>
            <a href="/support">Support</a>
          </div>
          <span className="muted" style={{ fontSize: 12 }}>© {new Date().getFullYear()} Outglow.Live</span>
        </div>
      </footer>

      <style>{`
        .landing { min-height: 100vh; }
        .land-nav {
          position: sticky; top: 0; z-index: 50;
          backdrop-filter: blur(12px);
          background: rgba(8, 6, 14, 0.78);
          border-bottom: 1px solid var(--line-soft);
          padding: 12px 0;
        }
        .land-nav-link {
          color: var(--muted); font-weight: 800; font-size: 13px;
          padding: 8px 12px; border-radius: 8px;
        }
        .land-nav-link:hover { color: var(--text); background: var(--card); text-decoration: none; }
        @media (max-width: 720px) {
          .land-nav-link { display: none; }
        }
        .hero { padding: 80px 0 60px; }
        .hero-inner {
          display: grid;
          grid-template-columns: 1.1fr 1fr;
          gap: 48px;
          align-items: center;
        }
        @media (max-width: 880px) {
          .hero-inner { grid-template-columns: 1fr; gap: 32px; }
          .hero-art { order: -1; }
        }
        .hero-copy { display: flex; flex-direction: column; gap: 18px; }
        .hero-lede { color: var(--text-dim); font-size: 17px; line-height: 1.6; max-width: 540px; margin: 0; }
        .section { padding: 72px 0; }
        .section-alt { background: linear-gradient(180deg, transparent, rgba(255, 62, 127, .04), transparent); }
        .how-grid {
          display: grid; grid-template-columns: repeat(3, 1fr);
          gap: 18px; margin-top: 28px;
        }
        @media (max-width: 760px) { .how-grid { grid-template-columns: 1fr; } }
        .feature-grid {
          display: grid; grid-template-columns: repeat(3, 1fr);
          gap: 16px; margin-top: 28px;
        }
        @media (max-width: 880px) { .feature-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 560px) { .feature-grid { grid-template-columns: 1fr; } }
        .tier-row {
          display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; margin-top: 28px;
        }
        @media (max-width: 760px) { .tier-row { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 420px) { .tier-row { grid-template-columns: repeat(2, 1fr); } }
        .cta-card {
          background:
            radial-gradient(circle at 0% 0%, rgba(255, 62, 127, .18), transparent 50%),
            radial-gradient(circle at 100% 100%, rgba(91, 233, 255, .14), transparent 50%),
            var(--card);
          border: 1px solid var(--line);
          border-radius: var(--radius-lg);
          padding: 36px;
          display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 24px;
        }
        @media (max-width: 720px) {
          .cta-card { grid-template-columns: 1fr; }
        }
        .footer {
          padding: 30px 0 50px;
          border-top: 1px solid var(--line-soft);
          margin-top: 32px;
        }
        .footer a { color: var(--muted); font-weight: 700; font-size: 13px; }
        .footer a:hover { color: var(--text); }

        /* ── Hero phone mockup ───────────────────────────────── */
        .phone-wrap {
          position: relative;
          display: grid; place-items: center;
          padding: 12px 0;
        }
        .phone-glow {
          position: absolute; inset: -10% 5%;
          background:
            radial-gradient(circle at 35% 25%, rgba(255,62,127,.40), transparent 55%),
            radial-gradient(circle at 70% 80%, rgba(91,233,255,.22), transparent 55%);
          filter: blur(46px);
          z-index: 0;
        }
        .phone {
          position: relative; z-index: 1;
          width: min(300px, 78vw);
          aspect-ratio: 9 / 19.2;
          background: #050309;
          border: 1px solid #2a2238;
          border-radius: 40px;
          padding: 10px;
          box-shadow:
            0 40px 90px rgba(0,0,0,.7),
            0 0 0 1px rgba(255,62,127,.10),
            inset 0 0 0 2px rgba(255,255,255,.02);
        }
        .phone-notch {
          position: absolute; top: 16px; left: 50%; transform: translateX(-50%);
          width: 92px; height: 22px; border-radius: 999px;
          background: #050309; z-index: 3;
        }
        .phone-screen {
          width: 100%; height: 100%;
          background:
            radial-gradient(circle at 0% 0%, rgba(255,62,127,.10), transparent 40%),
            var(--bg);
          border-radius: 30px;
          overflow: hidden;
          display: flex; flex-direction: column;
          padding: 14px 12px 0;
          position: relative;
        }
        .ph-status {
          display: flex; justify-content: space-between; align-items: center;
          font-size: 10px; font-weight: 800; color: var(--text-dim);
          padding: 2px 4px 8px;
        }
        .ph-status-icons { display: inline-flex; align-items: center; gap: 4px; }
        .ph-status-icons b { font-size: 8px; }
        .ph-sig, .ph-batt {
          display: inline-block; width: 14px; height: 8px;
          background: var(--text-dim); border-radius: 2px; opacity: .7;
        }
        .ph-batt { width: 16px; border-radius: 3px; }

        .ph-head {
          display: flex; align-items: flex-end; justify-content: space-between;
          padding: 2px 2px 10px;
        }
        .ph-title {
          font-family: var(--font-display); font-weight: 700;
          font-size: 22px; letter-spacing: -.6px; line-height: 1;
        }
        .ph-sub { font-size: 9px; color: var(--muted); font-weight: 700; margin-top: 4px; }
        .ph-live {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 9px; font-weight: 900; color: var(--primary);
          padding: 4px 8px; border-radius: 999px;
          background: rgba(255,62,127,.10); border: 1px solid rgba(255,62,127,.4);
        }
        .ph-live-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--primary); }

        .ph-seg {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px;
          padding: 3px; border-radius: 12px;
          background: var(--card); border: 1px solid var(--line-soft);
          margin-bottom: 10px;
        }
        .ph-seg span {
          display: inline-flex; align-items: center; justify-content: center; gap: 4px;
          font-size: 10px; font-weight: 900; color: var(--muted);
          padding: 7px 0; border-radius: 9px;
        }
        .ph-seg-on { color: #1a0a14 !important; background: var(--grad-glow); }

        .ph-chips { display: flex; gap: 6px; margin-bottom: 10px; }
        .ph-chip {
          font-size: 9px; font-weight: 800; color: var(--primary);
          padding: 5px 9px; border-radius: 999px;
          border: 1px solid rgba(255,62,127,.35);
        }
        .ph-chip-on { color: var(--text); background: rgba(255,62,127,.18); }

        .ph-card {
          background: var(--card); border: 1px solid var(--line-soft);
          border-radius: 16px; padding: 12px;
        }
        .ph-card-head {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 12px;
        }
        .ph-tags { font-size: 11px; font-weight: 900; }
        .ph-timer {
          font-size: 10px; font-weight: 900; color: var(--primary);
          letter-spacing: .5px;
          padding: 4px 8px; border-radius: 999px;
          border: 1px solid rgba(255,62,127,.4); background: rgba(255,62,127,.08);
        }
        .ph-vs-row { display: flex; align-items: center; gap: 8px; }
        .ph-side {
          flex: 1; background: var(--card-soft);
          border: 1px solid var(--line-soft); border-radius: 12px;
          padding: 10px; text-align: center;
        }
        .ph-photo {
          width: 46px; height: 46px; border-radius: 50%;
          margin: 0 auto 8px;
          border: 2px solid var(--line);
        }
        /* Layered gradients tuned to read as real photography:
           a warm poolside portrait + a cool sunset portrait. */
        .ph-photo-a {
          background:
            radial-gradient(circle at 50% 30%, #f4c9a8, transparent 45%),
            linear-gradient(160deg, #6fb7d4 0%, #2f6f8f 55%, #173b4d 100%);
        }
        .ph-photo-b {
          background:
            radial-gradient(circle at 50% 32%, #f6cdb0, transparent 45%),
            linear-gradient(160deg, #ffb37a 0%, #d76a8e 55%, #5b3b6e 100%);
        }
        .ph-name { font-size: 11px; font-weight: 900; }
        .ph-votes { font-size: 9px; color: var(--muted); font-weight: 700; margin-top: 2px; }
        .ph-vs {
          font-size: 9px; font-weight: 900; color: var(--muted);
          width: 22px; height: 22px; flex: none;
          display: grid; place-items: center;
          border-radius: 50%; background: var(--bg); border: 1px solid var(--line-soft);
        }
        .ph-bar {
          height: 4px; border-radius: 2px; margin-top: 12px;
          background: var(--card-soft); overflow: hidden;
        }
        .ph-bar span { display: block; width: 50%; height: 100%; background: var(--grad-glow); }

        .ph-tabs {
          margin-top: auto;
          display: grid; grid-template-columns: repeat(5, 1fr);
          align-items: center;
          padding: 10px 6px;
          border-top: 1px solid var(--line-soft);
        }
        .ph-tab {
          display: inline-flex; align-items: center; justify-content: center;
          color: var(--muted);
        }
        .ph-tab-on { color: var(--text); }
        .ph-tab-fab {
          width: 40px; height: 40px; border-radius: 13px;
          background: var(--grad-glow); color: #1a0a14;
          margin: 0 auto; box-shadow: 0 8px 18px rgba(255,62,127,.4);
        }

        /* ── Mini phone mockups (one per How-it-works step) ───── */
        .step-mock { margin-top: 6px; display: grid; place-items: center; }
        .mphone-wrap { position: relative; padding: 8px 0 4px; }
        .mphone-glow {
          position: absolute; inset: -8% 8%;
          background:
            radial-gradient(circle at 30% 30%, rgba(255,62,127,.28), transparent 55%),
            radial-gradient(circle at 75% 80%, rgba(91,233,255,.16), transparent 55%);
          filter: blur(28px); z-index: 0;
        }
        .mphone {
          position: relative; z-index: 1;
          width: 170px;
          aspect-ratio: 9 / 18;
          background: #050309;
          border: 1px solid #2a2238;
          border-radius: 24px;
          padding: 6px;
          box-shadow:
            0 20px 50px rgba(0,0,0,.55),
            inset 0 0 0 1px rgba(255,255,255,.02);
          margin: 0 auto;
        }
        .mphone-notch {
          position: absolute; top: 10px; left: 50%; transform: translateX(-50%);
          width: 52px; height: 12px; border-radius: 999px;
          background: #050309; z-index: 3;
        }
        .mphone-screen {
          width: 100%; height: 100%;
          background:
            radial-gradient(circle at 0% 0%, rgba(255,62,127,.08), transparent 40%),
            var(--bg);
          border-radius: 18px;
          overflow: hidden;
          padding: 18px 8px 6px;
          display: flex; flex-direction: column; gap: 6px;
        }
        .mph-bar {
          display: flex; align-items: center; justify-content: space-between;
          font-size: 9px; font-weight: 900; color: var(--text);
          padding: 0 2px 4px;
          border-bottom: 1px solid var(--line-soft);
        }
        .mph-bar :is(svg) { color: var(--muted); }
        .mph-bar > span:nth-child(2) { font-family: var(--font-display); font-weight: 700; }

        /* Step 1 — Forge */
        .mph-fg-grid {
          display: grid; grid-template-columns: 1fr 1fr; gap: 5px;
        }
        .mph-fg-tile {
          aspect-ratio: 1 / 1; border-radius: 8px;
          border: 1px solid var(--line-soft);
        }
        .mph-fg-photo {
          background:
            radial-gradient(circle at 50% 35%, #f4c9a8, transparent 45%),
            linear-gradient(160deg, #6fb7d4 0%, #2f6f8f 55%, #173b4d 100%);
          border-color: rgba(255,62,127,.30);
        }
        .mph-fg-add {
          background: var(--card-soft);
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 2px; color: var(--muted);
          font-size: 8px; font-weight: 800;
          border-style: dashed;
        }
        .mph-fg-input {
          font-size: 8px; font-weight: 700;
          padding: 6px 8px; border-radius: 6px;
          background: var(--card); border: 1px solid var(--line-soft);
          color: var(--text-dim);
        }
        .mph-fg-chips { display: flex; gap: 4px; }
        .mph-chip {
          font-size: 7px; font-weight: 900;
          padding: 3px 7px; border-radius: 999px;
          background: var(--card); border: 1px solid var(--line-soft);
          color: var(--muted);
        }
        .mph-chip-on {
          background: rgba(255,62,127,.10); color: var(--primary);
          border-color: rgba(255,62,127,.35);
        }
        .mph-fg-cta {
          margin-top: auto;
          text-align: center;
          font-size: 9px; font-weight: 900;
          padding: 7px 0; border-radius: 9px;
          color: #1a0a14; background: var(--grad-glow);
          box-shadow: 0 6px 14px rgba(255,62,127,.35);
        }

        /* Step 2 — Pick a target (post detail) */
        .mph-ch-author {
          display: flex; align-items: center; gap: 6px;
          padding: 4px 2px 0;
        }
        .mph-ch-avatar {
          width: 22px; height: 22px; border-radius: 50%;
          background:
            radial-gradient(circle at 50% 32%, #f6cdb0, transparent 45%),
            linear-gradient(160deg, #ffb37a 0%, #d76a8e 55%, #5b3b6e 100%);
          border: 1.5px solid rgba(255,62,127,.6);
        }
        .mph-ch-name { font-size: 9px; font-weight: 900; line-height: 1; }
        .mph-ch-handle { font-size: 7px; color: var(--muted); font-weight: 700; margin-top: 2px; }
        .mph-ch-media {
          flex: 1;
          border-radius: 8px;
          background:
            radial-gradient(circle at 50% 38%, #f6cdb0, transparent 45%),
            linear-gradient(160deg, #ffb37a 0%, #d76a8e 55%, #5b3b6e 100%);
          border: 1px solid var(--line-soft);
        }
        .mph-ch-actions {
          display: flex; gap: 8px;
          font-size: 8px; font-weight: 800; color: var(--muted);
        }
        .mph-ch-actions span { display: inline-flex; align-items: center; gap: 3px; }
        .mph-ch-cta {
          margin-top: auto;
          display: flex; align-items: center; justify-content: center; gap: 4px;
          font-size: 9px; font-weight: 900;
          padding: 8px 0; border-radius: 9px;
          color: #1a0a14; background: var(--grad-glow);
          box-shadow: 0 6px 16px rgba(255,62,127,.45);
        }

        /* Step 3 — Voting (battle detail) */
        .mph-v-tag {
          font-size: 8px; font-weight: 900; color: var(--primary);
          padding: 3px 7px; border-radius: 999px;
          background: rgba(255,62,127,.10);
          border: 1px solid rgba(255,62,127,.35);
          align-self: flex-start;
        }
        .mph-v-title { font-size: 11px; font-weight: 900; line-height: 1; }
        .mph-v-meta {
          display: flex; justify-content: space-between; align-items: center;
          font-size: 7px; color: var(--muted); font-weight: 800;
        }
        .mph-v-meta span { display: inline-flex; align-items: center; gap: 3px; }
        .mph-v-timer {
          color: var(--primary) !important;
          padding: 2px 6px; border-radius: 999px;
          border: 1px solid rgba(255,62,127,.35);
          background: rgba(255,62,127,.08);
        }
        .mph-v-side {
          background: var(--card); border: 1px solid var(--line-soft);
          border-radius: 8px; padding: 5px;
          display: flex; flex-direction: column; gap: 4px;
        }
        .mph-v-tier { font-size: 6px; font-weight: 900; color: var(--muted); letter-spacing: .8px; }
        .mph-v-photo {
          width: 100%; aspect-ratio: 16 / 9; border-radius: 5px;
        }
        .mph-v-photo-a {
          background:
            linear-gradient(135deg, #1a2f5c 0%, #0e1a36 60%),
            radial-gradient(circle at 30% 80%, #ffb37a, transparent 35%);
          background-blend-mode: screen;
        }
        .mph-v-photo-b {
          background:
            linear-gradient(135deg, #3a1828 0%, #1a0d18 60%),
            radial-gradient(circle at 70% 30%, #ff6b4a, transparent 40%);
          background-blend-mode: screen;
        }
        .mph-v-vs {
          align-self: center;
          font-size: 8px; font-weight: 900; color: var(--muted);
          width: 18px; height: 18px;
          display: grid; place-items: center;
          border-radius: 50%; background: var(--bg);
          border: 1px solid var(--line-soft);
        }
        .mph-v-bar {
          margin-top: auto;
          height: 3px; border-radius: 2px;
          background: var(--card-soft); overflow: hidden;
        }
        .mph-v-bar span {
          display: block; width: 62%; height: 100%;
          background: var(--grad-glow);
        }
      `}</style>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="stack-sm">
      <span style={{
        fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22,
        color: tone === 'primary' ? 'var(--primary)' : 'var(--text)'
      }}>{value}</span>
      <span className="muted" style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase' }}>{label}</span>
    </div>
  );
}

function IconTile({ name }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 42, height: 42, borderRadius: 12,
      background: 'rgba(255,62,127,.10)',
      border: '1px solid rgba(255,62,127,.22)',
      color: 'var(--primary)'
    }}>
      <Icon name={name} size={22} />
    </span>
  );
}

function Step({ n, title, body, icon, mockup }) {
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="row" style={{ gap: 12, alignItems: 'center' }}>
        <IconTile name={icon} />
        <span style={{
          fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16,
          color: 'var(--primary)'
        }}>STEP {n}</span>
      </div>
      <h3 style={{ fontWeight: 900, fontSize: 18, margin: 0 }}>{title}</h3>
      <p style={{ color: 'var(--text-dim)', margin: 0, lineHeight: 1.55 }}>{body}</p>
      {mockup ? <div className="step-mock">{mockup}</div> : null}
    </div>
  );
}

// Mini phone mockups — one per "how it works" step. Same visual
// language as the hero phone (rounded shell, faux notch, status
// bar, gradient FAB at the bottom) but compressed to fit inside
// the Step cards. Each one stages a different screen so the
// three steps read like a storyboard of the flow.
function MockPhone({ children }) {
  return (
    <div className="mphone-wrap">
      <div className="mphone-glow" />
      <div className="mphone">
        <div className="mphone-notch" />
        <div className="mphone-screen">
          {children}
        </div>
      </div>
    </div>
  );
}

function MockForge() {
  return (
    <MockPhone>
      <div className="mph-bar">
        <Icon name="arrowLeft" size={12} />
        <span>Forge</span>
        <span />
      </div>
      <div className="mph-fg-grid">
        <div className="mph-fg-tile mph-fg-photo" />
        <div className="mph-fg-tile mph-fg-add">
          <Icon name="plus" size={16} />
          <span>Add</span>
        </div>
      </div>
      <div className="mph-fg-input">A summer dip ☀️</div>
      <div className="mph-fg-chips">
        <span className="mph-chip mph-chip-on">Photography</span>
        <span className="mph-chip">Fashion</span>
      </div>
      <div className="mph-fg-cta">Drop a post</div>
    </MockPhone>
  );
}

function MockChallenge() {
  return (
    <MockPhone>
      <div className="mph-bar">
        <Icon name="arrowLeft" size={12} />
        <span>Post</span>
        <Icon name="more" size={12} />
      </div>
      <div className="mph-ch-author">
        <span className="mph-ch-avatar" />
        <div>
          <div className="mph-ch-name">Hannah</div>
          <div className="mph-ch-handle">@hannahjammah</div>
        </div>
      </div>
      <div className="mph-ch-media" />
      <div className="mph-ch-actions">
        <span><Icon name="flash" size={12} />128</span>
        <span><Icon name="chat" size={12} />14</span>
        <span><Icon name="send" size={12} /></span>
      </div>
      <div className="mph-ch-cta">
        <Icon name="flash" size={14} />
        Challenge
      </div>
    </MockPhone>
  );
}

function MockVote() {
  return (
    <MockPhone>
      <div className="mph-bar">
        <Icon name="arrowLeft" size={12} />
        <span>Battle</span>
        <span />
      </div>
      <div className="mph-v-tag">#cars</div>
      <div className="mph-v-title">Best whip?</div>
      <div className="mph-v-meta">
        <span><Icon name="user" size={9} />Open</span>
        <span className="mph-v-timer">23:59:51</span>
      </div>
      <div className="mph-v-side">
        <span className="mph-v-tier">CHALLENGER</span>
        <div className="mph-v-photo mph-v-photo-a" />
      </div>
      <div className="mph-v-vs">VS</div>
      <div className="mph-v-side">
        <span className="mph-v-tier">OPPONENT</span>
        <div className="mph-v-photo mph-v-photo-b" />
      </div>
      <div className="mph-v-bar"><span /></div>
    </MockPhone>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <IconTile name={icon} />
      <h3 style={{ fontWeight: 900, fontSize: 16, margin: 0 }}>{title}</h3>
      <p style={{ color: 'var(--muted)', margin: 0, fontSize: 14, lineHeight: 1.5 }}>{body}</p>
    </div>
  );
}

function Tier({ name, rep, color }) {
  return (
    <div className="card-soft" style={{ textAlign: 'center' }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: color, opacity: 0.85,
        margin: '0 auto 8px',
        boxShadow: `0 0 18px ${color}66`
      }} />
      <div style={{ fontWeight: 900, fontSize: 13 }}>{name}</div>
      <div className="muted" style={{ fontSize: 11, fontWeight: 700 }}>{rep} rep</div>
    </div>
  );
}

// Hero visual — a phone shell rendering a faithful reproduction of
// the app's Battles screen so the landing page shows the actual
// product, not abstract frames. Pure CSS (no screenshot assets to
// ship); the "photos" are layered gradients tuned to read as real
// imagery (pool / sunset / night-street) rather than emoji.
function HeroVisual() {
  return (
    <div className="phone-wrap">
      <div className="phone-glow" />
      <div className="phone">
        <div className="phone-notch" />
        <div className="phone-screen">
          {/* Status bar */}
          <div className="ph-status">
            <span>9:51</span>
            <span className="ph-status-icons">
              <i className="ph-sig" /><b>5G</b><i className="ph-batt" />
            </span>
          </div>

          {/* Battles header */}
          <div className="ph-head">
            <div>
              <div className="ph-title">Battles</div>
              <div className="ph-sub">1 battle · Active</div>
            </div>
            <span className="ph-live"><i className="ph-live-dot" />LIVE</span>
          </div>

          {/* Segment */}
          <div className="ph-seg">
            <span className="ph-seg-on"><Icon name="flash" size={12} /> Active</span>
            <span>Open</span>
            <span>Done</span>
          </div>

          {/* Hashtag chips */}
          <div className="ph-chips">
            <span className="ph-chip ph-chip-on">All</span>
            <span className="ph-chip">#pool</span>
            <span className="ph-chip">#sunset</span>
          </div>

          {/* Battle card */}
          <div className="ph-card">
            <div className="ph-card-head">
              <span className="ph-tags">#pool #sunset</span>
              <span className="ph-timer">23:59:34</span>
            </div>
            <div className="ph-vs-row">
              <div className="ph-side">
                <div className="ph-photo ph-photo-a" />
                <div className="ph-name">jellybelly</div>
                <div className="ph-votes">0 votes</div>
              </div>
              <span className="ph-vs">VS</span>
              <div className="ph-side">
                <div className="ph-photo ph-photo-b" />
                <div className="ph-name">Hannah</div>
                <div className="ph-votes">0 votes</div>
              </div>
            </div>
            <div className="ph-bar"><span /></div>
          </div>

          {/* Bottom tab bar */}
          <div className="ph-tabs">
            <span className="ph-tab"><Icon name="home" size={18} /></span>
            <span className="ph-tab"><Icon name="history" size={18} /></span>
            <span className="ph-tab ph-tab-fab"><Icon name="plus" size={20} /></span>
            <span className="ph-tab ph-tab-on"><Icon name="battles" size={18} /></span>
            <span className="ph-tab"><Icon name="user" size={18} /></span>
          </div>
        </div>
      </div>
    </div>
  );
}
